<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="supplier-analytics">
    <!-- Header Section -->
    <div class="analytics-header">
      <!-- Floating Bubbles -->
      <div class="bubbles-container">
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
        <span class="bubble" />
      </div>
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <feather-icon
              icon="UsersIcon"
              size="28"
            />
          </div>
          <div class="header-info">
            <h1 class="header-title">
              Performance Fournisseurs
            </h1>
            <p class="header-subtitle">
              Analytique détaillée par fournisseur et gouvernorat
            </p>
          </div>
        </div>
        <div class="header-actions">
          <div class="date-filters">
            <b-form-select
              v-model="selectedYear"
              :options="yearOptions"
              size="sm"
              class="filter-select"
              @change="fetchAnalytics"
            />
            <b-form-select
              v-model="selectedMonth"
              :options="monthOptions"
              size="sm"
              class="filter-select"
              @change="fetchAnalytics"
            />
          </div>
          <b-button
            variant="primary"
            size="sm"
            class="refresh-btn"
            :disabled="isLoading"
            @click="fetchAnalytics"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'spin-animation': isLoading }"
            />
            <span class="ml-50">{{ isLoading ? 'Chargement...' : 'Actualiser' }}</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <div class="loading-spinner">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
        <p class="loading-text">
          Chargement des données fournisseurs...
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="analytics-content"
    >
      <!-- Summary KPI Cards -->
      <div class="kpi-grid">
        <div class="kpi-card suppliers">
          <div class="kpi-icon">
            <feather-icon
              icon="UsersIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ summary.total_suppliers }}
            </h3>
            <p class="kpi-label">
              Fournisseurs Actifs
            </p>
            <div class="kpi-subvalue">
              ~{{ summary.avg_orders_per_supplier }} cmd/fournisseur
            </div>
          </div>
        </div>

        <div class="kpi-card orders">
          <div class="kpi-icon primary">
            <feather-icon
              icon="PackageIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_orders) }}
            </h3>
            <p class="kpi-label">
              Total Commandes
            </p>
          </div>
        </div>

        <div class="kpi-card delivered">
          <div class="kpi-icon success">
            <feather-icon
              icon="CheckCircleIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_delivered) }}
            </h3>
            <p class="kpi-label">
              Livrées
            </p>
            <div class="kpi-subvalue success">
              {{ summary.overall_delivery_rate }}% Taux
            </div>
          </div>
        </div>

        <div class="kpi-card returned">
          <div class="kpi-icon danger">
            <feather-icon
              icon="RotateCcwIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatNumber(summary.total_returned) }}
            </h3>
            <p class="kpi-label">
              Retours
            </p>
            <div class="kpi-subvalue warning">
              {{ summary.overall_return_rate }}% Taux
            </div>
          </div>
        </div>

        <div class="kpi-card revenue">
          <div class="kpi-icon warning">
            <feather-icon
              icon="DollarSignIcon"
              size="24"
            />
          </div>
          <div class="kpi-content">
            <h3 class="kpi-value">
              {{ formatCurrency(summary.total_revenue) }}
            </h3>
            <p class="kpi-label">
              Chiffre d'Affaires
            </p>
          </div>
        </div>
      </div>

      <!-- Top Section: Performance Distribution & Top Performers -->
      <div class="top-section">
        <!-- Performance Distribution -->
        <div class="card-panel">
          <div class="panel-header">
            <h3 class="panel-title">
              <feather-icon
                icon="BarChart2Icon"
                size="18"
              />
              Distribution des Performances
            </h3>
          </div>
          <div class="panel-body">
            <div class="distribution-chart">
              <div
                v-for="(count, level) in distribution"
                :key="level"
                class="distribution-item"
              >
                <div class="dist-header">
                  <span class="dist-label">{{ getDistributionLabel(level) }}</span>
                  <span
                    class="dist-count"
                    :class="getDistributionClass(level)"
                  >{{ count }}</span>
                </div>
                <div class="dist-bar-container">
                  <div
                    class="dist-bar"
                    :class="getDistributionClass(level)"
                    :style="{ width: getDistributionWidth(count) + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top Performers -->
        <div class="card-panel">
          <div class="panel-header">
            <h3 class="panel-title">
              <feather-icon
                icon="AwardIcon"
                size="18"
              />
              Top 5 Fournisseurs
            </h3>
          </div>
          <div class="panel-body">
            <div class="top-list">
              <div
                v-for="(supplier, index) in topPerformers"
                :key="supplier.id"
                class="top-item"
                @click="selectSupplier(supplier)"
              >
                <div class="top-rank">
                  <span
                    class="rank-badge"
                    :class="getRankClass(index)"
                  >{{ index + 1 }}</span>
                </div>
                <div class="top-info">
                  <h4 class="top-name">
                    {{ supplier.username }}
                  </h4>
                  <div class="top-stats">
                    <span class="stat">{{ supplier.stats.total_orders }} cmd</span>
                    <span class="stat success">{{ supplier.stats.delivery_rate }}%</span>
                  </div>
                </div>
                <div class="top-progress">
                  <div
                    class="progress-bar"
                    :style="{ width: supplier.stats.delivery_rate + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Needs Attention -->
      <div
        v-if="needsAttention.length > 0"
        class="attention-section"
      >
        <div class="attention-card">
          <div class="attention-header">
            <feather-icon
              icon="AlertTriangleIcon"
              size="20"
              class="attention-icon"
            />
            <h3 class="attention-title">
              Fournisseurs Nécessitant une Attention
            </h3>
            <span class="attention-badge">{{ needsAttention.length }}</span>
          </div>
          <div class="attention-body">
            <div class="attention-grid">
              <div
                v-for="supplier in needsAttention"
                :key="supplier.id"
                class="attention-item"
                @click="selectSupplier(supplier)"
              >
                <div class="attention-item-header">
                  <h4 class="attention-name">
                    {{ supplier.username }}
                  </h4>
                  <span class="attention-rate">{{ supplier.stats.return_rate }}% retour</span>
                </div>
                <div class="attention-item-stats">
                  <span>{{ supplier.stats.total_orders }} commandes</span>
                  <span class="danger">{{ supplier.stats.returned_count }} retours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Suppliers Table -->
      <div class="suppliers-section">
        <div class="table-card">
          <div class="table-header">
            <div class="table-title-section">
              <h3 class="table-title">
                <feather-icon
                  icon="ListIcon"
                  size="18"
                />
                Tous les Fournisseurs
              </h3>
              <span class="table-count">{{ suppliers.length }} fournisseurs</span>
            </div>
            <div class="table-actions">
              <b-form-input
                v-model="searchQuery"
                placeholder="Rechercher un fournisseur..."
                size="sm"
                class="search-input"
              />
              <b-button-group size="sm">
                <b-button
                  :variant="sortBy === 'total_orders' ? 'primary' : 'outline-primary'"
                  @click="sortTable('total_orders')"
                >
                  Commandes
                </b-button>
                <b-button
                  :variant="sortBy === 'delivery_rate' ? 'primary' : 'outline-primary'"
                  @click="sortTable('delivery_rate')"
                >
                  Performance
                </b-button>
                <b-button
                  :variant="sortBy === 'return_rate' ? 'primary' : 'outline-primary'"
                  @click="sortTable('return_rate')"
                >
                  Retours
                </b-button>
              </b-button-group>
            </div>
          </div>
          <div class="table-body">
            <div class="responsive-table">
              <table class="suppliers-table">
                <thead>
                  <tr>
                    <th>Fournisseur</th>
                    <th class="th-center">
                      Total
                    </th>
                    <th class="th-center">
                      Livrées
                    </th>
                    <th class="th-center">
                      Retours
                    </th>
                    <th class="th-center">
                      Taux Livraison
                    </th>
                    <th class="th-center">
                      Taux Retour
                    </th>
                    <th class="th-right">
                      Revenus
                    </th>
                    <th class="th-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="supplier in filteredSuppliers"
                    :key="supplier.id"
                    class="table-row"
                    :class="{ 'highlight-row': supplier.stats.delivery_rate >= 80 }"
                  >
                    <td>
                      <div class="supplier-cell">
                        <div class="supplier-avatar">
                          {{ supplier.username.charAt(0).toUpperCase() }}
                        </div>
                        <div class="supplier-info">
                          <span class="supplier-name">{{ supplier.username }}</span>
                          <span class="supplier-phone">{{ supplier.phone }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="td-center">
                      <span class="value-primary">{{ formatNumber(supplier.stats.total_orders) }}</span>
                    </td>
                    <td class="td-center">
                      <span class="value-success">{{ formatNumber(supplier.stats.delivered_count) }}</span>
                    </td>
                    <td class="td-center">
                      <span class="value-danger">{{ formatNumber(supplier.stats.returned_count) }}</span>
                    </td>
                    <td class="td-center">
                      <div class="rate-cell">
                        <div class="rate-bar-bg">
                          <div
                            class="rate-bar success"
                            :style="{ width: supplier.stats.delivery_rate + '%' }"
                          />
                        </div>
                        <span
                          class="rate-value"
                          :class="getDeliveryRateClass(supplier.stats.delivery_rate)"
                        >{{ supplier.stats.delivery_rate }}%</span>
                      </div>
                    </td>
                    <td class="td-center">
                      <div class="rate-cell">
                        <div class="rate-bar-bg">
                          <div
                            class="rate-bar danger"
                            :style="{ width: supplier.stats.return_rate + '%' }"
                          />
                        </div>
                        <span
                          class="rate-value"
                          :class="getReturnRateClass(supplier.stats.return_rate)"
                        >{{ supplier.stats.return_rate }}%</span>
                      </div>
                    </td>
                    <td class="td-right">
                      <span class="revenue-value">{{ formatCurrency(supplier.stats.total_revenue) }}</span>
                    </td>
                    <td class="td-center">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        class="detail-btn"
                        @click="selectSupplier(supplier)"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="14"
                        />
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Supplier Detail Modal -->
      <b-modal
        v-model="showDetailModal"
        size="xl"
        hide-footer
        centered
        title-class="modal-title-custom"
      >
        <template #modal-title>
          <div class="modal-title-content">
            <feather-icon
              icon="UserIcon"
              size="20"
            />
            <span>Détails: {{ selectedSupplier ? selectedSupplier.username : '' }}</span>
          </div>
        </template>

        <div
          v-if="selectedSupplier"
          class="supplier-detail"
        >
          <!-- Supplier Stats Summary -->
          <div class="detail-stats-grid">
            <div class="detail-stat">
              <div class="stat-icon">
                <feather-icon
                  icon="PackageIcon"
                  size="18"
                />
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ selectedSupplier.stats.total_orders }}</span>
                <span class="stat-label">Total Commandes</span>
              </div>
            </div>
            <div class="detail-stat success">
              <div class="stat-icon">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="18"
                />
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ selectedSupplier.stats.delivered_count }}</span>
                <span class="stat-label">Livrées ({{ selectedSupplier.stats.delivery_rate }}%)</span>
              </div>
            </div>
            <div class="detail-stat danger">
              <div class="stat-icon">
                <feather-icon
                  icon="RotateCcwIcon"
                  size="18"
                />
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ selectedSupplier.stats.returned_count }}</span>
                <span class="stat-label">Retours ({{ selectedSupplier.stats.return_rate }}%)</span>
              </div>
            </div>
            <div class="detail-stat warning">
              <div class="stat-icon">
                <feather-icon
                  icon="DollarSignIcon"
                  size="18"
                />
              </div>
              <div class="stat-content">
                <span class="stat-value">{{ formatCurrency(selectedSupplier.stats.total_revenue) }}</span>
                <span class="stat-label">Revenus</span>
              </div>
            </div>
          </div>

          <!-- Monthly Trend -->
          <div class="detail-section">
            <h4 class="section-title">
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
              />
              Tendance Mensuelle (6 mois)
            </h4>
            <div class="trend-chart">
              <div
                v-for="month in selectedSupplier.monthly_trend"
                :key="month.month"
                class="trend-bar-container"
              >
                <div class="trend-bars">
                  <div
                    class="trend-bar delivered"
                    :style="{ height: getTrendHeight(month.delivered) + 'px' }"
                    :title="`Livrées: ${month.delivered}`"
                  />
                  <div
                    class="trend-bar returned"
                    :style="{ height: getTrendHeight(month.returned) + 'px' }"
                    :title="`Retours: ${month.returned}`"
                  />
                </div>
                <span class="trend-label">{{ month.month }}</span>
                <span class="trend-total">{{ month.total }}</span>
              </div>
            </div>
            <div class="trend-legend">
              <span class="legend-item">
                <span class="legend-color delivered" />
                Livrées
              </span>
              <span class="legend-item">
                <span class="legend-color returned" />
                Retours
              </span>
            </div>
          </div>

          <!-- Governorate Breakdown -->
          <div class="detail-section">
            <h4 class="section-title">
              <feather-icon
                icon="MapPinIcon"
                size="16"
              />
              Performance par Gouvernorat
            </h4>
            <div class="gov-breakdown">
              <div
                v-for="gov in selectedSupplier.governorate_breakdown"
                :key="gov.governorate_id"
                class="gov-item"
              >
                <div class="gov-header">
                  <span class="gov-name">{{ gov.governorate_name }}</span>
                  <span class="gov-total">{{ gov.total_orders }} cmd</span>
                </div>
                <div class="gov-stats">
                  <div class="gov-stat">
                    <span class="gov-stat-label">Livrées</span>
                    <span class="gov-stat-value success">{{ gov.delivered_count }}</span>
                  </div>
                  <div class="gov-stat">
                    <span class="gov-stat-label">Retours</span>
                    <span class="gov-stat-value danger">{{ gov.returned_count }}</span>
                  </div>
                  <div class="gov-stat">
                    <span class="gov-stat-label">Taux</span>
                    <span
                      class="gov-stat-value"
                      :class="getDeliveryRateClass(gov.delivery_rate)"
                    >{{ gov.delivery_rate }}%</span>
                  </div>
                </div>
                <div class="gov-progress">
                  <div
                    class="gov-progress-bar"
                    :style="{ width: gov.delivery_rate + '%' }"
                    :class="getDeliveryRateClass(gov.delivery_rate)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Best & Worst Governorate -->
          <div
            v-if="selectedSupplier.top_governorate || selectedSupplier.worst_governorate"
            class="detail-highlights"
          >
            <div
              v-if="selectedSupplier.top_governorate"
              class="highlight-card success"
            >
              <feather-icon
                icon="ThumbsUpIcon"
                size="20"
              />
              <div class="highlight-content">
                <span class="highlight-label">Meilleure Performance</span>
                <span class="highlight-value">{{ selectedSupplier.top_governorate.governorate_name }}</span>
                <span class="highlight-stat">{{ selectedSupplier.top_governorate.delivery_rate }}% livraison</span>
              </div>
            </div>
            <div
              v-if="selectedSupplier.worst_governorate"
              class="highlight-card danger"
            >
              <feather-icon
                icon="AlertCircleIcon"
                size="20"
              />
              <div class="highlight-content">
                <span class="highlight-label">À Améliorer</span>
                <span class="highlight-value">{{ selectedSupplier.worst_governorate.governorate_name }}</span>
                <span class="highlight-stat">{{ selectedSupplier.worst_governorate.delivery_rate }}% livraison</span>
              </div>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BButton,
  BFormSelect,
  BFormInput,
  BButtonGroup,
  BModal,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'SupplierAnalytics',
  components: {
    BSpinner,
    BButton,
    BFormSelect,
    BFormInput,
    BButtonGroup,
    BModal,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      summary: {
        total_suppliers: 0,
        total_orders: 0,
        total_delivered: 0,
        total_returned: 0,
        total_revenue: 0,
        overall_delivery_rate: 0,
        overall_return_rate: 0,
        avg_orders_per_supplier: 0,
      },
      suppliers: [],
      topPerformers: [],
      needsAttention: [],
      distribution: {
        excellent: 0,
        good: 0,
        average: 0,
        poor: 0,
      },
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      searchQuery: '',
      sortBy: 'total_orders',
      sortDesc: true,
      showDetailModal: false,
      selectedSupplier: null,
    }
  },
  computed: {
    yearOptions() {
      const years = []
      const currentYear = new Date().getFullYear()
      for (let i = currentYear; i >= currentYear - 5; i -= 1) {
        years.push({ value: i, text: i.toString() })
      }
      return years
    },
    monthOptions() {
      return [
        { value: null, text: 'Tous les mois' },
        { value: 1, text: 'Janvier' },
        { value: 2, text: 'Février' },
        { value: 3, text: 'Mars' },
        { value: 4, text: 'Avril' },
        { value: 5, text: 'Mai' },
        { value: 6, text: 'Juin' },
        { value: 7, text: 'Juillet' },
        { value: 8, text: 'Août' },
        { value: 9, text: 'Septembre' },
        { value: 10, text: 'Octobre' },
        { value: 11, text: 'Novembre' },
        { value: 12, text: 'Décembre' },
      ]
    },
    filteredSuppliers() {
      let filtered = [...this.suppliers]

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(s => s.username.toLowerCase().includes(query)
          || (s.phone && s.phone.includes(query)))
      }

      filtered.sort((a, b) => {
        const aVal = a.stats[this.sortBy]
        const bVal = b.stats[this.sortBy]
        if (this.sortDesc) {
          return bVal - aVal
        }
        return aVal - bVal
      })

      return filtered
    },
  },
  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'admin') {
      this.fetchAnalytics()
    }
  },
  methods: {
    async fetchAnalytics() {
      try {
        this.isLoading = true
        let url = `/api/kpi/supplier-analytics/?year=${this.selectedYear}`
        if (this.selectedMonth) {
          url += `&month=${this.selectedMonth}`
        }
        const { data } = await axios.get(url)

        this.summary = data.summary || this.summary
        this.suppliers = data.suppliers || []
        this.topPerformers = data.top_performers || []
        this.needsAttention = data.needs_attention || []
        this.distribution = data.distribution || this.distribution
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching supplier analytics:', error)
        this.$bvToast.toast('Erreur lors du chargement des données', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoading = false
      }
    },
    formatNumber(value) {
      if (!value && value !== 0) return '0'
      return new Intl.NumberFormat('fr-FR').format(value)
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '0.000 TND'
      return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 3 }).format(value)} TND`
    },
    getDistributionLabel(level) {
      const labels = {
        excellent: 'Excellent (≥80%)',
        good: 'Bon (60-79%)',
        average: 'Moyen (40-59%)',
        poor: 'Faible (<40%)',
      }
      return labels[level] || level
    },
    getDistributionClass(level) {
      const classes = {
        excellent: 'dist-excellent',
        good: 'dist-good',
        average: 'dist-average',
        poor: 'dist-poor',
      }
      return classes[level] || ''
    },
    getDistributionWidth(count) {
      const total = Object.values(this.distribution).reduce((a, b) => a + b, 0)
      if (total === 0) return 0
      return Math.max((count / total) * 100, count > 0 ? 5 : 0)
    },
    getRankClass(index) {
      if (index === 0) return 'rank-gold'
      if (index === 1) return 'rank-silver'
      if (index === 2) return 'rank-bronze'
      return 'rank-default'
    },
    getDeliveryRateClass(rate) {
      if (rate >= 80) return 'rate-excellent'
      if (rate >= 60) return 'rate-good'
      if (rate >= 40) return 'rate-average'
      return 'rate-poor'
    },
    getReturnRateClass(rate) {
      if (rate <= 10) return 'rate-excellent'
      if (rate <= 20) return 'rate-good'
      if (rate <= 30) return 'rate-average'
      return 'rate-poor'
    },
    sortTable(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = field
        this.sortDesc = true
      }
    },
    selectSupplier(supplier) {
      this.selectedSupplier = supplier
      this.showDetailModal = true
    },
    getTrendHeight(value) {
      if (!this.selectedSupplier) return 0
      const maxValue = Math.max(
        ...this.selectedSupplier.monthly_trend.map(m => Math.max(m.delivered, m.returned, m.total)),
      )
      if (maxValue === 0) return 0
      return Math.max((value / maxValue) * 120, value > 0 ? 5 : 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.supplier-analytics {
  padding: 0;
  background: #ffffff;
  min-height: 100vh;
}

// Header with Light Red Animated Design
.analytics-header {
  position: relative;
  background: #ffffff;
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  border-radius: 1.5rem;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

// Floating Bubbles
.bubbles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.bubble {
  display: none;
}

@keyframes floatBubble {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
  25% {
    transform: translateY(-15px) scale(1.05);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-25px) scale(1);
    opacity: 0.5;
  }
  75% {
    transform: translateY(-10px) scale(1.08);
    opacity: 0.7;
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  background: linear-gradient(135deg, #360793 0%, #5a2bbd 100%);
  padding: 0.875rem;
  border-radius: 1rem;
  color: white;
  box-shadow: 0 4px 16px rgba(54, 7, 147, 0.25);

  &::before {
    display: none;
  }
}

@keyframes iconPulseHeader {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
  }
}

@keyframes iconGlow {
  0%, 100% {
    opacity: 0.55;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.header-info {
  color: #111111;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: #111111;
}

@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header-subtitle {
  font-size: 0.9rem;
  color: #555555;
  margin: 0.25rem 0 0;
}

@keyframes subtitleSlideIn {
  from {
    opacity: 0;
    transform: translateX(-15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.date-filters {
  display: flex;
  gap: 0.5rem;
}

.filter-select {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(10, 38, 52, 0.2);
  border-radius: 0.5rem;
  min-width: 100px;
  color: #2c3e50;
  transition: all 0.3s ease;

  &:focus {
    border-color: #0A2634;
    box-shadow: 0 0 0 3px rgba(10, 38, 52, 0.15);
  }
}

.refresh-btn {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  color: #0a0a0a;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  transition: all 0.3s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);
    background: #fff6f0;
  }

  &:disabled {
    opacity: 0.7;
    transform: none;
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Loading
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
}

.loading-text {
  margin-top: 1rem;
  color: #6c757d;
}

// Content
.analytics-content {
  padding: 0 1.5rem 2rem;
}

// KPI Grid
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.kpi-card {
  background: white;
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
  }

  &.suppliers::before { background: linear-gradient(to bottom, #11998e, #38ef7d); }
  &.orders::before { background: linear-gradient(to bottom, #667eea, #764ba2); }
  &.delivered::before { background: linear-gradient(to bottom, #28c76f, #1ea85a); }
  &.returned::before { background: linear-gradient(to bottom, #0A2634, #061a22); }
  &.revenue::before { background: linear-gradient(to bottom, #ff9f43, #f08c2e); }
}

.kpi-icon {
  background: rgba(17, 153, 142, 0.15);
  padding: 0.75rem;
  border-radius: 0.75rem;
  color: #11998e;
  flex-shrink: 0;

  &.primary {
    background: rgba(102, 126, 234, 0.15);
    color: #667eea;
  }
  &.success {
    background: #dcfce7;
    color: #3eb569;
  }
  &.danger {
    background: rgba(10, 26, 34, 0.15);
    color: #0a1a22;
  }
  &.warning {
    background: rgba(255, 159, 67, 0.15);
    color: #ff9f43;
  }
}

.kpi-content {
  flex: 1;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.2;
}

.kpi-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0.25rem 0 0;
}

.kpi-subvalue {
  font-size: 0.7rem;
  color: #6c757d;
  margin-top: 0.25rem;

  &.success { color: #28c76f; }
  &.warning { color: #ff9f43; }
}

// Top Section
.top-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.card-panel {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.panel-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}

.panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.panel-body {
  padding: 1.25rem;
}

// Distribution Chart
.distribution-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.distribution-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.dist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dist-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.dist-count {
  font-size: 0.85rem;
  font-weight: 600;

  &.dist-excellent { color: #28c76f; }
  &.dist-good { color: #00cfe8; }
  &.dist-average { color: #ff9f43; }
  &.dist-poor { color: #0A2634; }
}

.dist-bar-container {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.dist-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;

  &.dist-excellent { background: linear-gradient(90deg, #28c76f, #1ea85a); }
  &.dist-good { background: linear-gradient(90deg, #00cfe8, #00b8d4); }
  &.dist-average { background: linear-gradient(90deg, #ff9f43, #f08c2e); }
  &.dist-poor { background: linear-gradient(90deg, #0A2634, #061a22); }
}

// Top List
.top-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.top-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f2f5;
    transform: translateX(4px);
  }
}

.rank-badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;

  &.rank-gold { background: linear-gradient(135deg, #ffd700, #ffb800); color: #5c4a00; }
  &.rank-silver { background: linear-gradient(135deg, #c0c0c0, #a0a0a0); color: #4a4a4a; }
  &.rank-bronze { background: linear-gradient(135deg, #cd7f32, #b87333); color: white; }
  &.rank-default { background: #e0e0e0; color: #666; }
}

.top-info {
  flex: 1;
  min-width: 0;
}

.top-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.top-stats {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.125rem;
}

.stat {
  font-size: 0.75rem;
  color: #6c757d;

  &.success { color: #28c76f; font-weight: 600; }
}

.top-progress {
  width: 60px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #28c76f, #1ea85a);
  border-radius: 3px;
}

// Attention Section
.attention-section {
  margin-bottom: 1.5rem;
}

.attention-card {
  background: linear-gradient(135deg, #fff8f3 0%, #fff 100%);
  border: 1px solid #fecaca;
  border-radius: 1rem;
  overflow: hidden;
}

.attention-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: rgba(239, 68, 68, 0.1);
  border-bottom: 1px solid #fecaca;
}

.attention-icon {
  color: #ef4444;
}

.attention-title {
  font-size: 1rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
  flex: 1;
}

.attention-badge {
  background: #fef2f2;
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
}

.attention-body {
  padding: 1rem 1.25rem;
}

.attention-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.attention-item {
  background: white;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #f87171;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
  }
}

.attention-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.attention-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.attention-rate {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0A2634;
}

.attention-item-stats {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #6c757d;

  .danger { color: #0A2634; }
}

// Suppliers Table
.suppliers-section {
  margin-bottom: 1.5rem;
}

.table-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.table-count {
  font-size: 0.8rem;
  color: #6c757d;
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  min-width: 180px;
  border-radius: 0.5rem;
}

.table-body {
  padding: 0;
}

.responsive-table {
  overflow-x: auto;
}

.suppliers-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.875rem 1rem;
    text-align: left;
  }

  th {
    background: #f8f9fa;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e9ecef;

    &.th-center { text-align: center; }
    &.th-right { text-align: right; }
  }

  td {
    border-bottom: 1px solid #f0f0f0;

    &.td-center { text-align: center; }
    &.td-right { text-align: right; }
  }
}

.table-row {
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &.highlight-row {
    background: rgba(40, 199, 111, 0.05);
  }
}

.supplier-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.supplier-avatar {
  width: 36px;
  height: 36px;
  border-radius: 0.625rem;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.supplier-info {
  display: flex;
  flex-direction: column;
}

.supplier-name {
  font-weight: 600;
  color: #2c3e50;
}

.supplier-phone {
  font-size: 0.75rem;
  color: #6c757d;
}

.value-primary { color: #2c3e50; font-weight: 600; }
.value-success { color: #28c76f; font-weight: 600; }
.value-danger { color: #0A2634; font-weight: 600; }

.rate-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.rate-bar-bg {
  width: 50px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.rate-bar {
  height: 100%;
  border-radius: 3px;

  &.success { background: linear-gradient(90deg, #28c76f, #1ea85a); }
  &.danger { background: linear-gradient(90deg, #0A2634, #061a22); }
}

.rate-value {
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 40px;

  &.rate-excellent { color: #28c76f; }
  &.rate-good { color: #00cfe8; }
  &.rate-average { color: #ff9f43; }
  &.rate-poor { color: #0A2634; }
}

.revenue-value {
  font-weight: 600;
  color: #ff9f43;
  font-size: 0.85rem;
}

.detail-btn {
  padding: 0.375rem 0.5rem;
}

// Modal Styles
.modal-title-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.supplier-detail {
  padding: 0.5rem;
}

.detail-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.detail-stat {
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &.success { background: rgba(40, 199, 111, 0.1); }
  &.danger { background: rgba(10, 38, 52, 0.1); }
  &.warning { background: rgba(255, 159, 67, 0.1); }
}

.stat-icon {
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
}

.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

// Trend Chart
.trend-chart {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 160px;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
}

.trend-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
}

.trend-bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 120px;
}

.trend-bar {
  width: 16px;
  border-radius: 2px 2px 0 0;
  transition: height 0.3s ease;

  &.delivered { background: linear-gradient(to top, #28c76f, #48da89); }
  &.returned { background: linear-gradient(to top, #0A2634, #f08182); }
}

.trend-label {
  font-size: 0.65rem;
  color: #6c757d;
}

.trend-total {
  font-size: 0.7rem;
  font-weight: 600;
  color: #2c3e50;
}

.trend-legend {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 0.75rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;

  &.delivered { background: #28c76f; }
  &.returned { background: #0A2634; }
}

// Governorate Breakdown
.gov-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}

.gov-item {
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.gov-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.gov-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.gov-total {
  font-size: 0.75rem;
  color: #6c757d;
}

.gov-stats {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.gov-stat {
  display: flex;
  flex-direction: column;
}

.gov-stat-label {
  font-size: 0.65rem;
  color: #6c757d;
}

.gov-stat-value {
  font-size: 0.85rem;
  font-weight: 600;

  &.success { color: #28c76f; }
  &.danger { color: #0A2634; }
}

.gov-progress {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
}

.gov-progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;

  &.rate-excellent { background: #28c76f; }
  &.rate-good { background: #00cfe8; }
  &.rate-average { background: #ff9f43; }
  &.rate-poor { background: #0A2634; }
}

// Detail Highlights
.detail-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.highlight-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.75rem;

  &.success {
    background: rgba(40, 199, 111, 0.1);
    color: #28c76f;
  }

  &.danger {
    background: rgba(10, 38, 52, 0.1);
    color: #0A2634;
  }
}

.highlight-content {
  display: flex;
  flex-direction: column;
}

.highlight-label {
  font-size: 0.7rem;
  opacity: 0.8;
}

.highlight-value {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.highlight-stat {
  font-size: 0.75rem;
}

// Responsive
@media (max-width: 768px) {
  .analytics-header {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .analytics-content {
    padding: 0 1rem 1.5rem;
  }

  .top-section {
    grid-template-columns: 1fr;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .table-actions {
    width: 100%;
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }
}
</style>
